/**
 * 62.47: a wind generator (the owner, Oct 10: "I guess a wind generator system"). Decision 120 gave each planet one wind
 * vector per weather segment (core/weather.ts planetWind). This varies it in time and place, from one source:
 *
 * - **gusts** over time: a seeded, smooth value noise over the seconds of the segment — the strength swells and ebbs
 *   (WIND_GUST.strength either side of 1) and the direction drifts a little (WIND_GUST.driftRadians) — keyed by the
 *   match seed, the planet and the segment, so every host reads the same gust at the same tick;
 * - **shelter** by place: lower in a hollow or a crater and behind a rise (the ground around the point stands higher),
 *   higher on a crest, lower inside a stand of trees (WIND_SHELTER).
 *
 * Readers: the shells take the planet's gusting wind (the kernel holds one vector per planet, so the gust is planet-wide
 * and the shelter is not theirs — decision 120's switch gates it, a version with it); the smoke clouds drift with the wind
 * where they stand (65.15; the same switch); the wind sound (67.26) and what sways (the stands, the grass) read the level
 * at the viewer's place — presentation, nothing the sim sees. Deterministic: dmath and integer hashing only.
 */
import type { GameState, Vec3 } from '../types';
import { WIND_GUST, WIND_SHELTER } from '../constants/action';
import { WEATHER_EFFECTS, weatherSegment, planetWeather } from './weather';
import { windField, windFieldAt } from './windField';
import { vecAdd, vecCross, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { G, PHYS_DT } from '../constants/physics';
import { dcos, dsin } from './dmath';
import { hashKey } from './random';
import { getSurfaceRadius } from './surface';
import { cellOf, cellCentre } from './cells';
import { propsWithin } from './hits';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';

type WindState = Pick<GameState, 'config' | 'turnNumber' | 'planets'>;

/** A lattice value in [0, 1) from an integer key and a lattice index — the gust's noise. */
function lattice(key: number, i: number): number {
  let h = Math.imul(key ^ Math.imul(i, 0x9e3779b9), 0x85ebca6b);
  h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
/** Smooth value noise over `x` (one period per unit): the two neighbouring lattice values eased together. */
function smooth(key: number, x: number): number {
  const i = Math.floor(x), f = x - i, t = f * f * (3 - 2 * f);
  return lattice(key, i) * (1 - t) + lattice(key, i + 1) * t;
}

/**
 * 62.64: the gust's key, `hashKey` of the seed, the planet and the segment — kept per planet id for the last seed and
 * segment it was asked for (every bullet asked it every tick: a template string and its hash each time). The same key.
 */
const gustKeys = new Map<number, { seed: unknown; segment: number; key: number }>();
function gustKey(seed: unknown, planetId: number, segment: number): number {
  const last = gustKeys.get(planetId);
  if (last && last.seed === seed && last.segment === segment) return last.key;
  const key = hashKey(`${seed}-${planetId}-gust-${segment}`);
  gustKeys.set(planetId, { seed, segment, key });
  return key;
}

/** The gust at `seconds` into a planet's segment: a strength factor round 1 and a direction drift in radians. */
export function gustAt(state: WindState, planetIndex: number, seconds: number): { strength: number; drift: number } {
  const planet = state.planets[planetIndex];
  if (!planet) return { strength: 1, drift: 0 };
  const key = gustKey(state.config.seed, planet.id, weatherSegment(state, seconds));
  const x = seconds / WIND_GUST.periodSeconds;
  const strength = 1 + (smooth(key, x) * 2 - 1) * WIND_GUST.strength;
  const drift = (smooth(key ^ 0x5bd1e995, x * 0.7 + 11.5) * 2 - 1) * WIND_GUST.driftRadians;
  return { strength, drift };
}


/** The gust on a field vector at a place: its direction swung by the drift about the place's up, its size × the strength. */
function gusted(state: WindState, planetIndex: number, place: Vec3, base: Vec3, seconds: number): Vec3 {
  return gustWith(gustParts(state, planetIndex, seconds), place, base);
}
/** A gust at a second, with its drift's turn (69.67: taken once for many places). */
interface GustParts { strength: number; drift: number; cos: number; sin: number }
function gustParts(state: WindState, planetIndex: number, seconds: number): GustParts {
  const { strength, drift } = gustAt(state, planetIndex, seconds);
  return { strength, drift, cos: dcos(drift), sin: dsin(drift) };
}
function gustWith(g: GustParts, place: Vec3, base: Vec3): Vec3 {
  if (g.drift === 0) return vecScale(base, g.strength);
  const n = vecNormalize(place);   // about the up, Rodrigues' rotation of a vector along the ground is base·cos + (n × base)·sin
  return vecScale(vecAdd(vecScale(base, g.cos), vecScale(vecCross(n, base), g.sin)), g.strength);
}


/**
 * 62.54 step 4: every planet's gust now for the kernel — [strength, cos(drift), sin(drift)] a planet (dmath, so every
 * engine swings the grid's wind the same). Empty without the wind switch.
 */
export function windGusts(state: WindState, seconds = 0): Float64Array {
  if (!state.config.wind) return new Float64Array(0);
  const out = new Float64Array(state.planets.length * 3);
  for (let i = 0; i < state.planets.length; i++) {
    const { strength, drift } = gustAt(state, i, seconds);
    out[i * 3] = strength; out[i * 3 + 1] = dcos(drift); out[i * 3 + 2] = dsin(drift);
  }
  return out;
}

/** The field at a place with the gust, before the shelter (what flies high — a drone — feels: the ground's shelter is not its). Null when off. */
export function gustedField(state: WindState, planetIndex: number, dir: Vec3, seconds = 0): Vec3 | null {
  const base = windField(state, planetIndex, dir, seconds);
  return base ? gusted(state, planetIndex, dir, base, seconds) : null;
}

/**
 * The shelter where someone stands: 1 in the open; less in a hollow, a crater or behind higher ground (the ground a few
 * people out stands higher than the point); more on a crest; less inside a stand of trees. Pure over the terrain and
 * the props.
 */
export function shelterAt(state: Pick<GameState, 'config' | 'planets' | 'rocks' | 'flora' | 'structures'>, planetIndex: number, dir: Vec3): number {
  // 62.54 step 2: the shelter map — the shelter is a pure function of the ground and the trees, so it is read per cell
  // (core/cells.ts, ~WIND_SHELTER.cellPeople across), each cell's value the exact shelter at its centre, derived on first
  // ask and kept per heightmap and flora (a crater or a felled tree starts the planet's map afresh). The value never
  // depends on who asked first, so the sim and the presentation share it.
  const planet = state.planets[planetIndex];
  if (!planet) return 1;
  const person = PERSON_BODY_HEIGHT * personSetOf(state as Pick<GameState, 'config'>).k;
  let memo = shelterMemo.get(planet.heightmap);
  if (!memo || memo.planetIndex !== planetIndex || memo.person !== person) {
    memo = { planetIndex, person, n: Math.max(8, Math.ceil((2 * planet.radius) / (WIND_SHELTER.cellPeople * person))), ground: new Map(), byFlora: new WeakMap() };
    shelterMemo.set(planet.heightmap, memo);
  }
  const floraKey: object = state.flora ?? NO_FLORA;
  let cells = memo.byFlora.get(floraKey);
  if (!cells) { cells = new Map(); memo.byFlora.set(floraKey, cells); }
  const cell = cellOf(dir.x, dir.y, dir.z, memo.n);
  let v = cells.get(cell);
  if (v === undefined) {
    // 62.64: the ground's share of the cell is kept per heightmap (it reads no tree), so a new flora list — the forest's
    // bands joining, ~every 10 ticks in a fight — recounts only the cell's trees; the same operations as shelterExact
    let g = memo.ground.get(cell);
    if (!g) { g = shelterGround(state, planetIndex, cellCentre(cell, memo.n)); memo.ground.set(cell, g); }
    v = shelterInStand(state, planetIndex, g);
    cells.set(cell, v);
  }
  return v;
}

/**
 * Per heightmap: the planet's shelter map (step 2) — the cell count per face edge, each asked cell's ground share (62.64:
 * shared by every flora list on this ground) and, per flora list, the cells' shelter.
 */
const shelterMemo = new WeakMap<Float32Array, { planetIndex: number; person: number; n: number; ground: Map<number, ShelterGround>; byFlora: WeakMap<object, Map<number, number>> }>();
const NO_FLORA = {};

/** The shelter exactly at a place (62.47's rule): what the map samples at each cell's centre. */
export function shelterExact(state: Pick<GameState, 'config' | 'planets' | 'rocks' | 'flora' | 'structures'>, planetIndex: number, dir: Vec3): number {
  if (!state.planets[planetIndex]) return 1;
  return shelterInStand(state, planetIndex, shelterGround(state, planetIndex, dir));
}

/** 62.64: shelterExact's ground share at a place — the rule's hollow, crest and rise (no tree read) — and the place's up and ground radius (the stand is counted round the ground point there, taken from the planet as it stands). */
interface ShelterGround { shelter: number; n: Vec3; here: number }
function shelterGround(state: Pick<GameState, 'config' | 'planets'>, planetIndex: number, dir: Vec3): ShelterGround {
  const planet = state.planets[planetIndex];
  const ps = personSetOf(state as Pick<GameState, 'config'>);
  const person = PERSON_BODY_HEIGHT * ps.k;
  const n = vecNormalize(dir);
  const here = getSurfaceRadius(planet, n);
  // the ground around: four points WIND_SHELTER.reachPeople out along the tangent frame
  const ref = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const u = vecNormalize(vecCross(n, ref)), v = vecCross(n, u);
  const arc = (WIND_SHELTER.reachPeople * person) / planet.radius;
  let around = 0;
  for (const [a, b] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
    const d = vecNormalize(vecAdd(vecScale(n, dcos(arc)), vecScale(vecAdd(vecScale(u, a), vecScale(v, b)), dsin(arc))));
    around += getSurfaceRadius(planet, d);
  }
  const rise = (here - around / 4) / person;   // people above (+) or below (−) the ground around
  const shelter = rise >= 0 ? 1 + Math.min(WIND_SHELTER.crestMax - 1, rise * WIND_SHELTER.perPersonUp) : Math.max(WIND_SHELTER.hollowMin, 1 + rise * WIND_SHELTER.perPersonDown);
  return { shelter, n, here };
}

/** 62.64: shelterExact's last step — inside a stand (trees within reach of the ground point), the ground share × the stand's factor. */
function shelterInStand(state: Pick<GameState, 'config' | 'planets' | 'rocks' | 'flora' | 'structures'>, planetIndex: number, g: ShelterGround): number {
  const person = PERSON_BODY_HEIGHT * personSetOf(state as Pick<GameState, 'config'>).k;
  let shelter = g.shelter;
  const pt = vecAdd(state.planets[planetIndex].position, vecScale(g.n, g.here));
  const trees = propsWithin(pt, WIND_SHELTER.standReachPeople * person, planetIndex, state).filter(p => p.ref.kind === 'flora').length;
  if (trees >= WIND_SHELTER.standTrees) shelter *= WIND_SHELTER.standFactor;
  return shelter;
}

/** The wind at a place and a moment: the planet's gusting wind × the shelter there. Null when calm or off. */
export function windAt(state: Pick<GameState, 'config' | 'turnNumber' | 'planets' | 'rocks' | 'flora' | 'structures'>, planetIndex: number, dir: Vec3, seconds = 0): Vec3 | null {
  // 62.54 step 3: the field at this place (bands + cells), gusted, × the shelter here
  const base = windField(state, planetIndex, dir, seconds);
  if (!base) return null;
  return vecScale(gusted(state, planetIndex, dir, base, seconds), shelterAt(state, planetIndex, dir));
}

/** The wind's level at a place, 0–1 against a storm's wind in the open. Reads the weather even with the wind switch off, so the sound and what sways follow the weather whether or not the shells feel it. */
export function windLevelAt(state: Pick<GameState, 'config' | 'turnNumber' | 'planets' | 'rocks' | 'flora' | 'structures'>, planetIndex: number, dir: Vec3, seconds = 0): number {
  return windHere(state, planetIndex, dir, seconds).level;
}

/**
 * The wind where a viewer stands, for presentation: `factor` is the gust × the shelter (1 = the weather's usual wind
 * there, 0 when the air is calm), `level` the same against a storm's wind in the open (0–1, for what sways).
 */
export function windHere(state: Pick<GameState, 'config' | 'turnNumber' | 'planets' | 'rocks' | 'flora' | 'structures'>, planetIndex: number, dir: Vec3, seconds = 0): { factor: number; level: number } {
  const kind = planetWeather(state, planetIndex, seconds);
  if (kind === null) return { factor: 0, level: 0 };
  const share = WEATHER_EFFECTS[kind].wind;
  if (state.config.wind) {
    // 62.54 step 3: with the wind on, the field here is the wind: its level against a storm's, and its factor against the weather's usual wind
    const w = windAt(state, planetIndex, dir, seconds), planet = state.planets[planetIndex];
    if (!w || !planet) return { factor: 0, level: 0 };
    const level = Math.max(0, Math.min(1, vecLength(w) / stormWind(planet)));
    return { factor: share > 0 ? Math.min(2, (level * WEATHER_EFFECTS.storm.wind) / share) : 1, level };
  }
  if (share <= 0) return { factor: 0, level: 0 };
  const factor = gustAt(state, planetIndex, seconds).strength * shelterAt(state, planetIndex, dir);
  return { factor, level: Math.max(0, Math.min(1, (share * factor) / WEATHER_EFFECTS.storm.wind)) };
}

// ---------------------------------------------------------------------------
// 62.52: wind on the action clock — what flies there feels windAt (gust × shelter)
// ---------------------------------------------------------------------------

type ActionWindState = Pick<GameState, 'config' | 'turnNumber' | 'planets' | 'rocks' | 'flora' | 'structures'> & Partial<Pick<GameState, 'tick'>>;

/** The action clock's second the wind is read at: the state's tick (the same second every reader of a tick takes). */
export function windSeconds(state: Partial<Pick<GameState, 'tick'>>): number { return (state.tick ?? 0) * PHYS_DT; }

/**
 * 62.52: the wind's pull on something flying at `pos` on the action clock (bullets, rockets, grenades): `windAt` there
 * (the gust × the shelter under it), taken along the planet's tangent plane and faded to nothing one radius up, as the
 * kernel takes it on a shell (lib.rs wind_accel — the kernel flies shells; TypeScript flies these, from the same
 * source), × the person set's felt gravity (a person-scale thing feels the felt world, as its fall does) × `scale` (the
 * thing's own share: ACTION_WIND). Null with the wind switch off, in calm air, or off the planet's air. `seconds`: the
 * wind's time (now by default; 65.25e: the aim's preview asks it along a flight still to come).
 */
export function actionWindAccel(state: ActionWindState, planetIndex: number, pos: Vec3, scale: number, seconds = windSeconds(state)): Vec3 | null {
  if (!state.config.wind || scale === 0) return null;
  const planet = state.planets[planetIndex];
  if (!planet || planet.alive === false) return null;
  return accelWith(state, planetIndex, pos, scale, d => windAt(state, planetIndex, d, seconds), personSetOf(state).gravityScale);
}

/**
 * 69.67 (a strike's lay: the frozen field asked thousands of times a call, each ask its planet's frame, cells, gust and
 * person set looked up again): `actionWindAccel` at one second for many places — what no place changes taken once, each
 * place's the same arithmetic. Always a function (asked where the wind is off or calm, it answers null, as the other does).
 */
export function actionWindAccelAt(state: ActionWindState, planetIndex: number, scale: number, seconds: number): (pos: Vec3) => Vec3 | null {
  if (!state.config.wind || scale === 0) return () => null;
  const planet = state.planets[planetIndex];
  if (!planet || planet.alive === false) return () => null;
  const field = windFieldAt(state, planetIndex, seconds), gust = gustParts(state, planetIndex, seconds), gravityScale = personSetOf(state).gravityScale;
  const wind = (d: Vec3): Vec3 | null => {
    const base = field ? field(d) : null;
    return base ? vecScale(gustWith(gust, d, base), shelterAt(state, planetIndex, d)) : null;   // (windAt's)
  };
  return pos => accelWith(state, planetIndex, pos, scale, wind, gravityScale);
}

/** actionWindAccel's pull at a place, the wind there asked of `wind`. */
function accelWith(state: ActionWindState, planetIndex: number, pos: Vec3, scale: number, wind: (n: Vec3) => Vec3 | null, gravityScale: number): Vec3 | null {
  const planet = state.planets[planetIndex];
  const r = vecSub(pos, planet.position);
  const dist = vecLength(r);
  if (dist < 1e-9) return null;
  const height = dist - planet.radius;
  if (height >= planet.radius) return null;
  const n = vecScale(r, 1 / dist);
  const w = wind(n);
  if (!w) return null;
  const tangent = vecSub(w, vecScale(n, vecDot(w, n)));
  const fade = height <= 0 ? 1 : 1 - height / planet.radius;
  return vecScale(tangent, fade * gravityScale * scale);
}

/** The surface gravity a storm's wind is a share of on a planet (WEATHER_EFFECTS.storm.wind × G·M / r²): the wind's full scale. */
export function stormWind(planet: Pick<GameState['planets'][number], 'mass' | 'radius'>): number {
  return WEATHER_EFFECTS.storm.wind * G * planet.mass / (planet.radius * planet.radius);
}
